import React, { useState, useEffect, useMemo } from 'react';
import { db } from './firebase';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

// ----------------------------------------------------------------------
// COMPONENTE: TARJETA EJECUTIVA (CERO ICONOS, PURA TIPOGRAFÍA)
// ----------------------------------------------------------------------
function ExecCard({ title, value, subtitle, accentColor, isNegative = false }) {
    return (
        <div className="bg-white p-8 rounded-[2rem] shadow-sm border border-gray-100 hover:shadow-xl transition-all duration-500 relative overflow-hidden group flex flex-col justify-between h-40">
            <div className={`absolute top-0 left-0 w-full h-1 ${accentColor} transform origin-left transition-transform duration-500 scale-x-0 group-hover:scale-x-100`}></div>
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest z-10">{title}</p>
            <p className={`text-4xl font-black z-10 tracking-tighter ${isNegative ? 'text-red-500' : 'text-kokimiko-texto'}`}>{value}</p>
            {subtitle && <p className="text-xs font-bold text-gray-400 uppercase tracking-widest z-10">{subtitle}</p>}
        </div>
    );
}

// ----------------------------------------------------------------------
// FORMATEADOR DE MONEDA
// ----------------------------------------------------------------------
const formatCurrency = (value) => {
    return Number(value || 0).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });
};

// ----------------------------------------------------------------------
// VISTA PRINCIPAL
// ----------------------------------------------------------------------
function ReportesView() {
    const [cargando, setCargando] = useState(true);
    const [ventasMostrador, setVentasMostrador] = useState([]);
    const [eventos, setEventos] = useState([]);
    const [compras, setCompras] = useState([]);
    
    // Fechas por defecto: Mes actual
    const [fechaInicio, setFechaInicio] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
    const [fechaFin, setFechaFin] = useState(new Date());

    // 1. CARGA DE DATOS (LAS 3 ARTERIAS DE KOKIMIKO)
    useEffect(() => {
        const unsubMostrador = onSnapshot(query(collection(db, 'mostrador_ventas')), snap => {
            setVentasMostrador(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });
        
        // Solo traemos los presupuestos que ya son un ingreso real (Aprobados)
        const unsubEventos = onSnapshot(query(collection(db, 'presupuestos'), where('estado', '==', 'Aprobado')), snap => {
            setEventos(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });

        const unsubCompras = onSnapshot(query(collection(db, 'compras')), snap => {
            setCompras(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });

        const timer = setTimeout(() => setCargando(false), 1500);
        return () => { unsubMostrador(); unsubEventos(); unsubCompras(); clearTimeout(timer); };
    }, []);

    // 2. MOTOR DE FILTRADO Y MATEMÁTICAS
    const metricas = useMemo(() => {
        const finDelDia = new Date(fechaFin);
        finDelDia.setHours(23, 59, 59, 999);

        // Filtrar Mostrador
        const mostradorFiltrado = ventasMostrador.filter(v => {
            if (!v.fecha) return false;
            const fechaVenta = v.fecha.toDate ? v.fecha.toDate() : new Date(v.fecha);
            return fechaVenta >= fechaInicio && fechaVenta <= finDelDia;
        });

        // Filtrar Eventos (Cotizaciones Aprobadas)
        const eventosFiltrados = eventos.filter(e => {
            if (!e.fecha && !e.fechaPresupuesto) return false;
            const fechaEv = new Date(e.fecha || e.fechaPresupuesto);
            return fechaEv >= fechaInicio && fechaEv <= finDelDia;
        });

        // Filtrar Compras (Gastos)
        const comprasFiltradas = compras.filter(c => {
            if (!c.fecha) return false;
            const fechaCompra = c.fecha.toDate ? c.fecha.toDate() : new Date(c.fecha);
            return fechaCompra >= fechaInicio && fechaCompra <= finDelDia;
        });

        // Sumatorias
        const totalMostrador = mostradorFiltrado.reduce((acc, v) => acc + (parseFloat(v.monto) || 0), 0);
        const totalEventos = eventosFiltrados.reduce((acc, e) => acc + (parseFloat(e.totales?.totalGeneral || e.calculos?.totalGeneral || 0)), 0);
        const totalIngresosBrutos = totalMostrador + totalEventos;
        const totalEgresos = comprasFiltradas.reduce((acc, c) => acc + (parseFloat(c.costoTotal) || 0), 0);
        const margenNeto = totalIngresosBrutos - totalEgresos;

        // Construir datos para la gráfica (Agrupación por día)
        const graficaMap = {};
        
        // Inyectar Mostrador a la gráfica
        mostradorFiltrado.forEach(v => {
            const dateObj = v.fecha.toDate ? v.fecha.toDate() : new Date(v.fecha);
            const key = dateObj.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
            if (!graficaMap[key]) graficaMap[key] = { fecha: key, sortKey: dateObj.getTime(), Ingresos: 0, Gastos: 0 };
            graficaMap[key].Ingresos += (parseFloat(v.monto) || 0);
        });

        // Inyectar Eventos a la gráfica
        eventosFiltrados.forEach(e => {
            const dateObj = new Date(e.fecha || e.fechaPresupuesto);
            const key = dateObj.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
            if (!graficaMap[key]) graficaMap[key] = { fecha: key, sortKey: dateObj.getTime(), Ingresos: 0, Gastos: 0 };
            graficaMap[key].Ingresos += (parseFloat(e.totales?.totalGeneral || e.calculos?.totalGeneral || 0));
        });

        // Inyectar Gastos a la gráfica
        comprasFiltradas.forEach(c => {
            const dateObj = c.fecha.toDate ? c.fecha.toDate() : new Date(c.fecha);
            const key = dateObj.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
            if (!graficaMap[key]) graficaMap[key] = { fecha: key, sortKey: dateObj.getTime(), Ingresos: 0, Gastos: 0 };
            graficaMap[key].Gastos += (parseFloat(c.costoTotal) || 0);
        });

        const graficaData = Object.values(graficaMap).sort((a, b) => a.sortKey - b.sortKey);

        return { totalMostrador, totalEventos, totalIngresosBrutos, totalEgresos, margenNeto, graficaData, mostradorFiltrado };
    }, [ventasMostrador, eventos, compras, fechaInicio, fechaFin]);

    // ----------------------------------------------------------------------
    // RENDERIZADO
    // ----------------------------------------------------------------------
    if (cargando) {
        return (
            <div className="flex justify-center items-center h-full bg-kokimiko-fondo/30">
                <div className="text-center font-black text-kokimiko-texto uppercase tracking-widest animate-pulse">
                    <p className="text-2xl">Sincronizando Nodos</p>
                    <p className="text-xs text-kokimiko-teal mt-2">Calculando Flujo de Caja...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="h-full flex flex-col p-4 md:p-8 gap-8 bg-kokimiko-fondo/30 overflow-y-auto font-sans">
            
            {/* CABECERA MINIMALISTA */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 bg-white p-6 rounded-[2rem] shadow-sm border border-gray-100">
                <div>
                    <h1 className="text-3xl md:text-4xl font-black text-kokimiko-texto uppercase tracking-tighter">Métricas / P&L</h1>
                    <p className="text-xs font-bold text-gray-400 mt-2 uppercase tracking-widest">Análisis Financiero Global</p>
                </div>
                
                <div className="flex flex-col md:flex-row items-center gap-4">
                    <div className="flex flex-col">
                        <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Periodo Desde</span>
                        <DatePicker 
                            selected={fechaInicio} 
                            onChange={date => setFechaInicio(date)} 
                            dateFormat="dd/MM/yyyy" 
                            className="bg-gray-50 border border-gray-200 text-kokimiko-texto font-bold px-4 py-2 rounded-xl focus:outline-none focus:border-kokimiko-teal transition-colors w-32 text-center" 
                        />
                    </div>
                    <span className="text-gray-300 font-bold hidden md:block">/</span>
                    <div className="flex flex-col">
                        <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Periodo Hasta</span>
                        <DatePicker 
                            selected={fechaFin} 
                            onChange={date => setFechaFin(date)} 
                            dateFormat="dd/MM/yyyy" 
                            className="bg-gray-50 border border-gray-200 text-kokimiko-texto font-bold px-4 py-2 rounded-xl focus:outline-none focus:border-kokimiko-teal transition-colors w-32 text-center" 
                        />
                    </div>
                </div>
            </div>

            {/* TARJETAS EJECUTIVAS */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
                <ExecCard 
                    title="Ingresos Mostrador" 
                    value={formatCurrency(metricas.totalMostrador)} 
                    subtitle={`${metricas.mostradorFiltrado.length} tickets emitidos`}
                    accentColor="bg-kokimiko-teal" 
                />
                <ExecCard 
                    title="Ingresos Cumpleaños" 
                    value={formatCurrency(metricas.totalEventos)} 
                    subtitle="Eventos facturados (Aprobados)"
                    accentColor="bg-kokimiko-azul" 
                />
                <ExecCard 
                    title="Fuga de Capital (Compras)" 
                    value={`- ${formatCurrency(metricas.totalEgresos)}`} 
                    subtitle="Gastos operativos y almacén"
                    accentColor="bg-red-500" 
                    isNegative={true}
                />
                <ExecCard 
                    title="Flujo de Caja Neto" 
                    value={formatCurrency(metricas.margenNeto)} 
                    subtitle="Ingresos Totales - Fuga de Capital"
                    accentColor={metricas.margenNeto >= 0 ? 'bg-green-500' : 'bg-red-500'} 
                />
            </div>

            {/* GRÁFICA DE RENDIMIENTO */}
            <div className="bg-white p-8 rounded-[2rem] shadow-sm border border-gray-100">
                <div className="mb-8">
                    <h2 className="text-sm font-black text-kokimiko-texto uppercase tracking-widest">Balance de Operaciones</h2>
                    <p className="text-xs text-gray-400 font-bold uppercase tracking-widest mt-1">Comparativa Diaria de Entradas vs Salidas</p>
                </div>
                <div className="h-96 w-full">
                    {metricas.graficaData.length > 0 ? (
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={metricas.graficaData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="colorIngresos" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#1BB1A5" stopOpacity={0.3}/>
                                        <stop offset="95%" stopColor="#1BB1A5" stopOpacity={0}/>
                                    </linearGradient>
                                    <linearGradient id="colorGastos" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#E15A42" stopOpacity={0.3}/>
                                        <stop offset="95%" stopColor="#E15A42" stopOpacity={0}/>
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                <XAxis dataKey="fecha" axisLine={false} tickLine={false} tick={{fill: '#a0aec0', fontSize: 10, fontWeight: 'bold'}} dy={10} />
                                <YAxis axisLine={false} tickLine={false} tick={{fill: '#a0aec0', fontSize: 10, fontWeight: 'bold'}} tickFormatter={(value) => `${value}€`} dx={-10} />
                                <Tooltip 
                                    contentStyle={{ borderRadius: '1rem', border: 'none', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)', fontWeight: 'bold', fontSize: '12px' }}
                                    formatter={(value) => [`${value.toLocaleString()} €`, '']}
                                />
                                <Area type="monotone" dataKey="Ingresos" stroke="#1BB1A5" strokeWidth={3} fillOpacity={1} fill="url(#colorIngresos)" />
                                <Area type="monotone" dataKey="Gastos" stroke="#E15A42" strokeWidth={3} fillOpacity={1} fill="url(#colorGastos)" />
                            </AreaChart>
                        </ResponsiveContainer>
                    ) : (
                        <div className="w-full h-full flex items-center justify-center border-2 border-dashed border-gray-100 rounded-2xl">
                            <p className="text-gray-400 font-bold text-xs uppercase tracking-widest">No hay movimientos registrados en estas fechas.</p>
                        </div>
                    )}
                </div>
            </div>

        </div>
    );
}

export default ReportesView;